import { isPlanId } from 'src/utils/plan/plan-service-lookup';

import { PlanService, IWorkspacePlan } from 'src/constants/plan-service';

/** planId 별칭 표(`IWorkspacePlan.planId`)를 문자열 키로 연다 — 값은 `PlanService` 의 행 키 */
const PLAN_ID_TABLE: Record<string, string> = IWorkspacePlan.planId;

/**
 * subscriptionPlan.planId 등을 PlanService 키(plan_growth 등)로 정규화합니다.
 * @param {string | null | undefined} planId
 * @returns {string | null}
 */
export function normalizePlanId(planId: string | null | undefined): string | null {
  if (!planId || typeof planId !== 'string') return null;

  const trimmed = planId.trim();
  if (!trimmed) return null;

  if (isPlanId(trimmed)) return trimmed;

  const byPlanKey = PLAN_ID_TABLE[trimmed];
  if (isPlanId(byPlanKey)) return byPlanKey;

  const byDisplayName = Object.entries(IWorkspacePlan.planName).find(
    ([, name]) => name.toLowerCase() === trimmed.toLowerCase(),
  );
  if (byDisplayName && isPlanId(byDisplayName[0])) return byDisplayName[0];

  const byKoreanName = Object.entries(IWorkspacePlan.planNameKor).find(
    ([, name]) => name === trimmed,
  );
  if (byKoreanName && isPlanId(byKoreanName[0])) return byKoreanName[0];

  const normalizedToken = trimmed.toLowerCase().replace(/\s+/g, '');
  const prefixedPlanId = normalizedToken.startsWith('plan_')
    ? normalizedToken
    : `plan_${normalizedToken}`;

  if (isPlanId(prefixedPlanId)) return prefixedPlanId;

  return null;
}

/**
 * 레거시 planId·planName·planType 문자열에서 플랜을 추론합니다.
 * @param {string} rawValue
 * @returns {string | null}
 */
function inferPlanIdFromLegacyString(rawValue: unknown): string | null {
  const field = String(rawValue || '').trim().toLowerCase();
  if (!field) return null;

  if (field.includes('enterprise')) return IWorkspacePlan.planId.plan_enterprise;
  if (field.includes('growth') || field.includes('그로스')) {
    return IWorkspacePlan.planId.plan_growth;
  }
  if (field.includes('brand') || field.includes('브랜드')) {
    return IWorkspacePlan.planId.plan_brand;
  }
  if (field.includes('individual') || field.includes('개인')) {
    return IWorkspacePlan.planId.plan_individual;
  }
  if (field.includes('trial') || field.includes('체험')) {
    return IWorkspacePlan.planId.plan_trial;
  }
  if (field.includes('demo') || field.includes('데모')) {
    return IWorkspacePlan.planId.plan_demo;
  }

  return null;
}

/**
 * subscriptionPlan 객체에서 PlanService 키를 해석합니다.
 * @param {Object | null | undefined} subscriptionPlan
 * @returns {string | null}
 */
export function resolveSubscriptionPlanId(subscriptionPlan: any): string | null {
  if (!subscriptionPlan) return null;

  const candidates = [
    subscriptionPlan.planId,
    subscriptionPlan.planName,
    subscriptionPlan.planType,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const resolved = normalizePlanId(candidates[i]);
    if (resolved) return resolved;
  }

  for (let i = 0; i < candidates.length; i += 1) {
    const inferred = inferPlanIdFromLegacyString(candidates[i]);
    if (inferred) return inferred;
  }

  return null;
}

const BRAND_TIER_PLAN_IDS = new Set<unknown>([
  IWorkspacePlan.planId.plan_brand,
  IWorkspacePlan.planId.plan_growth,
]);

/**
 * Brand·Growth 등 브랜드급 플랜 여부 (전용 채널·브랜드 기능 포함)
 * @param {string | null | undefined} planId
 * @returns {boolean}
 */
export function isBrandTierPlan(planId: string | null | undefined) {
  return BRAND_TIER_PLAN_IDS.has(planId);
}

/**
 * Brand 플랜 전용 채널(@cosme·LIPS) 사용 가능 여부 — Brand·체험(Growth 제외)
 * @param {string | null | undefined} planId
 * @returns {boolean}
 */
export function canUseBrandExclusivePlatforms(planId: string | null | undefined) {
  return planId === IWorkspacePlan.planId.plan_brand
    || planId === IWorkspacePlan.planId.plan_trial;
}

/**
 * 브랜드 멘션 Slack·데일리/위클리 이메일 알림 (Growth·Brand 등)
 * @param {string | null | undefined} planId
 * @returns {boolean}
 */
export function canUseBrandSlackAndWeeklyReport(planId: string | null | undefined) {
  if (!planId) return false;
  if (planId === IWorkspacePlan.planId.plan_individual) return false;
  return isBrandTierPlan(planId)
    || planId === IWorkspacePlan.planId.plan_trial
    || planId === IWorkspacePlan.planId.plan_enterprise;
}

/**
 * Instagram 스토리 수집 가능 여부 (Growth·Brand·Enterprise·Trial)
 * @param {string | null | undefined} planId
 * @returns {boolean}
 */
export function canCollectInstagramStory(planId: string | null | undefined) {
  if (!planId) return false;
  return isBrandTierPlan(planId)
    || planId === IWorkspacePlan.planId.plan_enterprise
    || planId === IWorkspacePlan.planId.plan_trial;
}

/**
 * Instagram 계정 연동(Unipile) UI 사용 가능 여부 (Growth·Brand·Enterprise·Trial)
 * @param {string | null | undefined} planId
 * @returns {boolean}
 */
export function canUseInstagramAccountConnect(planId: string | null | undefined) {
  if (!planId) return false;
  return isBrandTierPlan(planId)
    || planId === IWorkspacePlan.planId.plan_enterprise
    || planId === IWorkspacePlan.planId.plan_trial;
}

/**
 * 플랜별 Instagram 계정 연동(Unipile) 최대 개수 (PlanService 기본값)
 * Brand·Enterprise는 subscriptionPlan.planService로 커스텀 가능 — resolvePlanServiceValue / getLimit 사용
 * Growth 2 · Brand 5 · Enterprise 20 · Trial 1 · 그 외 0
 * @param {string | null | undefined} planId
 * @returns {number}
 */
export function getInstagramAccountConnectLimit(planId: string | null | undefined) {
  if (!canUseInstagramAccountConnect(planId)) return 0;
  // 타입 좁히기용 · 도달 불가(위 함수가 `!planId` 면 false 를 준다)
  if (!planId) return 0;
  const value = Number(isPlanId(planId) ? PlanService[planId].brandAccount?.instagramAccountConnectCount : undefined);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Growth 등 — 브랜드 전용 기능에서만 국가 고정 (검색·트렌드 등 공용 기능 제외)
 * @param {string | null | undefined} planId
 * @returns {string | null}
 */
export function getBrandFeatureFixedCountry(planId: string | null | undefined) {
  if (planId === IWorkspacePlan.planId.plan_growth) return 'KR';
  return null;
}

/**
 * 플랜별 국가 고정을 필터 객체에 반영
 *
 * ⚠️ 반환은 **입력 모양 그대로**라 제네릭이다. `Object` 로 좁히면 TS 호출부가
 * 어떤 필드도 읽지 못한다.
 *
 * @template {object} T
 * @param {T} [filter]
 * @param {string | null | undefined} fixedCountry
 * @returns {T}
 */
export function applyFixedCountryToFilter<T extends object>(
  filter?: T,
  fixedCountry?: string | null,
): T {
  // `filter ?? {}` 를 «한 번만» 읽는다 — 순수식이라 결과는 같다. `as T` 는 JSDoc 이 적어 둔 계약 그대로다
  const base = (filter ?? {}) as T;
  if (!fixedCountry) return base;
  return { ...base, country: [fixedCountry] };
}

/** theme.palette.error.main과 동일 (테마 컨텍스트 밖 fallback) */
const ENTERPRISE_ACCENT_COLOR = '#FF5630';

/**
 * Enterprise는 error 테마 색상, 그 외 플랜은 mainColor를 반환합니다.
 *
 * ⚠️ enterprise 가 아니면 `mainColor` 를 **그대로 돌려준다** — 못 찾은 플랜을
 * 넘기면 `undefined` 가 나온다. 부르는 쪽(`PlanFeatureValue`)의 `?? 'text.primary'`
 * 가 그 갈래다. 계약을 `string` 으로 적으면 그 폴백이 죽은 코드로 보인다.
 *
 * @param {string} planId
 * @param {string|undefined} mainColor
 * @param {import('@mui/material/styles').Theme} [theme]
 * @returns {string|undefined}
 */
export function getPlanAccentColor(
  planId: string | null | undefined,
  mainColor: string | undefined,
  theme?: { palette?: { error?: { main?: string } } },
): string | undefined {
  if (planId === 'plan_enterprise') {
    return theme?.palette?.error?.main ?? ENTERPRISE_ACCENT_COLOR;
  }
  return mainColor;
}
